import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Live2DApp } from '../lib/live2d/Live2DApp';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const LIVE2D_CORE_SCRIPT = process.env.PUBLIC_URL + '/live2d/live2dcubismcore.min.js';
const MOTION_OPTIONS = [
    { label: 'Idle (標準)', group: 'Idle', index: 0 },
    { label: 'Flick Up 1', group: 'FlickUp', index: 0 },
    { label: 'Flick Up 2', group: 'FlickUp', index: 1 },
    { label: 'Flick 1', group: 'Flick', index: 0 },
    { label: 'Flick 2', group: 'Flick', index: 1 },
    { label: 'Tap 1', group: 'Tap', index: 0 },
    { label: 'Tap 2', group: 'Tap', index: 1 },
    { label: 'Tap 3', group: 'Tap', index: 2 },
    { label: 'Tap 4', group: 'Tap', index: 3 },
    { label: 'Flick3 1', group: 'Flick3', index: 0 },
    { label: 'Flick3 2', group: 'Flick3', index: 1 },
    { label: 'FlickDown 1', group: 'FlickDown', index: 0 },
    { label: 'FlickDown 2', group: 'FlickDown', index: 1 },
    { label: 'Shake 1', group: 'Shake', index: 0 },
    { label: 'Shake 2', group: 'Shake', index: 1 }
];
export default function Live2DViewer({ audioFile, sequence = null, displayLabel, overlay, autoPlayToken, stopToken, onSequenceEnded }) {
    const canvasRef = useRef(null);
    const audioRef = useRef(null);
    const controllerRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaSourceRef = useRef(null);
    const analyserRef = useRef(null);
    const frequencyDataRef = useRef(null);
    const timeDomainDataRef = useRef(null);
    const smoothingRef = useRef(0);
    const rafRef = useRef();
    const timelineTimeoutsRef = useRef([]);
    const sequenceStartTimeRef = useRef(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLive2DReady, setIsLive2DReady] = useState(false);
    const [analyserReady, setAnalyserReady] = useState(false);
    const [error, setError] = useState('');
    const [selectedMotionKey, setSelectedMotionKey] = useState(() => `${MOTION_OPTIONS[0].group}:${MOTION_OPTIONS[0].index}`);
    const [selectedExpression, setSelectedExpression] = useState('');
    const [isCompactLayout, setIsCompactLayout] = useState(false);
    const activeAudioFile = sequence?.audioFile ?? audioFile ?? '';
    const audioQuery = sequence?.audioQuery ?? '';
    const playbackRate = sequence?.playbackRate ?? 1;
    const preservePitch = sequence?.preservePitch;
    const statusLabel = displayLabel ??
        (sequence ? sequence.name : audioFile || '音声ファイル未選択');
    const sequenceRef = useRef(sequence);
    const controlsStyle = isCompactLayout
        ? {
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            alignItems: 'stretch'
        }
        : {
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            padding: '16px',
            flexWrap: 'wrap',
            width: '100%',
            boxSizing: 'border-box'
        };
    const controlGroupStyle = isCompactLayout
        ? {
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            width: '100%'
        }
        : {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
        };
    const primaryButtonStyle = isCompactLayout
        ? {
            padding: '10px 20px',
            fontSize: '16px',
            width: '100%',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: !activeAudioFile || !isLive2DReady ? 'not-allowed' : 'pointer'
        }
        : {
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: !activeAudioFile || !isLive2DReady ? 'not-allowed' : 'pointer'
        };
    useEffect(() => {
        sequenceRef.current = sequence;
    }, [sequence]);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const handleResize = () => setIsCompactLayout(window.innerWidth <= 720);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    useEffect(() => {
        let active = true;
        const init = async () => {
            try {
                if (!canvasRef.current)
                    return;
                if (!window.Live2DCubismCore) {
                    await loadScript(LIVE2D_CORE_SCRIPT);
                }
                const controller = new Live2DApp();
                controllerRef.current = controller;
                await controller.initialize(canvasRef.current);
                if (active) {
                    setIsLive2DReady(true);
                }
                else {
                    controller.dispose();
                }
            }
            catch (err) {
                console.error(err);
                if (active) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            }
        };
        init();
        return () => {
            active = false;
            controllerRef.current?.dispose();
            controllerRef.current = null;
            smoothingRef.current = 0;
        };
    }, []);
    const audioInitializedRef = useRef(false);
    const clearSequenceTimeline = useCallback(() => {
        timelineTimeoutsRef.current.forEach(id => clearTimeout(id));
        timelineTimeoutsRef.current = [];
    }, []);
    const applySequenceMotion = useCallback((motion, onFinished) => {
        if (!motion)
            return;
        controllerRef.current?.startMotion(motion.group, motion.index, motion.priority, onFinished);
    }, []);
    const scheduleSequenceEvents = useCallback(() => {
        const currentSequence = sequenceRef.current;
        if (!currentSequence)
            return;
        const audioElement = audioRef.current;
        if (!audioElement || !controllerRef.current)
            return;
        clearSequenceTimeline();
        const events = currentSequence.events ?? [];
        events.forEach(event => {
            const eventTime = typeof event.time === 'number' ? event.time : 0;
            if (typeof event.time !== 'number') {
                console.warn(`イベントにtimeが設定されていません。0秒として扱います: ${currentSequence.name}`);
            }
            const remaining = eventTime - audioElement.currentTime;
            if (remaining < 0) {
                return;
            }
            const timeoutId = window.setTimeout(() => {
                const controller = controllerRef.current;
                const activeSequence = sequenceRef.current;
                if (!controller || !activeSequence)
                    return;
                if (activeSequence.name !== currentSequence.name) {
                    return;
                }
                if (event.stopAllMotions) {
                    controller.stopAllMotions();
                }
                if (event.expression) {
                    controller.setExpression(event.expression);
                }
                const runCompletion = () => {
                    if (!event.onComplete)
                        return;
                    const latestController = controllerRef.current;
                    const latestSequence = sequenceRef.current;
                    if (!latestController || !latestSequence)
                        return;
                    if (latestSequence.name !== currentSequence.name)
                        return;
                    if (event.onComplete.expression) {
                        latestController.setExpression(event.onComplete.expression);
                    }
                    if (event.onComplete.motion) {
                        applySequenceMotion(event.onComplete.motion);
                    }
                    if (event.onComplete.stopAllMotions) {
                        latestController.stopAllMotions();
                    }
                };
                if (event.motion) {
                    applySequenceMotion(event.motion, runCompletion);
                }
                else {
                    runCompletion();
                }
            }, remaining * 1000);
            timelineTimeoutsRef.current.push(timeoutId);
        });
    }, [clearSequenceTimeline, applySequenceMotion]);
    useEffect(() => {
        return () => clearSequenceTimeline();
    }, [clearSequenceTimeline]);
    useEffect(() => {
        clearSequenceTimeline();
    }, [sequence, clearSequenceTimeline]);
    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement)
            return;
        if (audioInitializedRef.current) {
            return () => { };
        }
        audioInitializedRef.current = true;
        if (!audioContextRef.current) {
            audioContextRef.current = new AudioContext();
        }
        const context = audioContextRef.current;
        if (!context) {
            setError('AudioContext initialization failed');
            return () => { };
        }
        let sourceNode = mediaSourceRef.current;
        if (!sourceNode) {
            sourceNode = context.createMediaElementSource(audioElement);
            mediaSourceRef.current = sourceNode;
        }
        const analyser = context.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.25;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -6;
        sourceNode.connect(analyser);
        analyser.connect(context.destination);
        analyserRef.current = analyser;
        setAnalyserReady(true);
        const handlePlay = async () => {
            setIsPlaying(true);
            sequenceStartTimeRef.current = performance.now();
            if (context.state === 'suspended') {
                try {
                    await context.resume();
                }
                catch (resumeError) {
                    console.warn('AudioContext resume failed', resumeError);
                }
            }
            const currentSequence = sequenceRef.current;
            if (currentSequence) {
                if (audioElement.currentTime < 0.05) {
                    if (currentSequence.initialExpression) {
                        controllerRef.current?.setExpression(currentSequence.initialExpression);
                    }
                    applySequenceMotion(currentSequence.initialMotion);
                }
                scheduleSequenceEvents();
            }
        };
        const handlePause = () => {
            setIsPlaying(false);
            clearSequenceTimeline();
            sequenceStartTimeRef.current = 0;
        };
        const handleEnded = () => {
            const elapsed = performance.now() - sequenceStartTimeRef.current;
            const playedTime = audioElement.currentTime;
            sequenceStartTimeRef.current = 0;
            setIsPlaying(false);
            clearSequenceTimeline();
            const currentSequence = sequenceRef.current;
            if (currentSequence) {
                const completionExpression = currentSequence.completion?.expression ??
                    currentSequence.initialExpression;
                const completionMotion = currentSequence.completion?.motion ?? currentSequence.initialMotion;
                if (completionExpression) {
                    controllerRef.current?.setExpression(completionExpression);
                }
                applySequenceMotion(completionMotion);
            }
            if (playedTime < 0.05 && elapsed < 300) {
                console.log('⚠️ Ignoring spurious ended event', { playedTime, elapsed });
                return;
            }
            if (onSequenceEnded) {
                onSequenceEnded();
            }
        };
        audioElement.addEventListener('play', handlePlay);
        audioElement.addEventListener('pause', handlePause);
        audioElement.addEventListener('ended', handleEnded);
        return () => {
            audioInitializedRef.current = false;
            audioElement.pause();
            audioElement.removeEventListener('play', handlePlay);
            audioElement.removeEventListener('pause', handlePause);
            audioElement.removeEventListener('ended', handleEnded);
            try {
                analyser.disconnect();
                sourceNode?.disconnect();
            }
            catch (disconnectError) {
                console.warn('Failed to disconnect audio graph', disconnectError);
            }
            analyserRef.current = null;
            setAnalyserReady(false);
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
            setIsPlaying(false);
            smoothingRef.current = 0;
            controllerRef.current?.setLipSyncValue(0);
            setAnalyserReady(false);
            clearSequenceTimeline();
            sequenceStartTimeRef.current = 0;
        };
    }, [clearSequenceTimeline, scheduleSequenceEvents, applySequenceMotion]);
    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement)
            return;
        if (!activeAudioFile) {
            audioElement.pause();
            audioElement.removeAttribute('src');
            setIsPlaying(false);
            smoothingRef.current = 0;
            controllerRef.current?.setLipSyncValue(0);
            sequenceStartTimeRef.current = 0;
            return;
        }
        let audioUrl = `${API_URL}/audio_clipped/${activeAudioFile}`;
        if (audioQuery) {
            audioUrl += audioUrl.includes('?') ? `&${audioQuery}` : `?${audioQuery}`;
        }
        console.log('Loading audio from:', audioUrl);
        console.log('API_URL:', API_URL);
        console.log('activeAudioFile:', activeAudioFile);
        audioElement.crossOrigin = 'anonymous';
        audioElement.src = audioUrl;
        audioElement.load();
        audioElement.playbackRate = playbackRate;
        const preserve = preservePitch !== false;
        if ('preservesPitch' in audioElement) {
            audioElement.preservesPitch = preserve;
        }
        if ('mozPreservesPitch' in audioElement) {
            audioElement.mozPreservesPitch = preserve;
        }
        if ('webkitPreservesPitch' in audioElement) {
            audioElement.webkitPreservesPitch = preserve;
        }
        setIsPlaying(false);
        smoothingRef.current = 0;
        controllerRef.current?.setLipSyncValue(0);
    }, [activeAudioFile, audioQuery, playbackRate, preservePitch]);
    useEffect(() => {
        if (!isLive2DReady || !analyserReady) {
            return;
        }
        let active = true;
        const analyser = analyserRef.current;
        const controller = controllerRef.current;
        if (!analyser || !controller) {
            return;
        }
        const tick = () => {
            if (!active)
                return;
            let freqData = frequencyDataRef.current;
            if (!freqData || freqData.length !== analyser.frequencyBinCount) {
                freqData = new Uint8Array(analyser.frequencyBinCount);
                frequencyDataRef.current = freqData;
            }
            analyser.getByteFrequencyData(freqData);
            let freqSum = 0;
            for (let i = 0; i < freqData.length; i += 1) {
                freqSum += freqData[i];
            }
            const normalizedFreq = freqSum / freqData.length / 255;
            let timeData = timeDomainDataRef.current;
            if (!timeData || timeData.length !== analyser.fftSize) {
                timeData = new Float32Array(analyser.fftSize);
                timeDomainDataRef.current = timeData;
            }
            analyser.getFloatTimeDomainData(timeData);
            let sumSquares = 0;
            for (let i = 0; i < timeData.length; i += 1) {
                const sample = timeData[i];
                sumSquares += sample * sample;
            }
            const rms = Math.sqrt(sumSquares / timeData.length);
            const volume = Math.min(1, rms * 2.2 + normalizedFreq * 0.9);
            smoothingRef.current = smoothingRef.current * 0.6 + volume * 0.4;
            controller.setLipSyncValue(smoothingRef.current);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            active = false;
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = undefined;
            }
        };
    }, [isLive2DReady, analyserReady]);
    const startSequencePlayback = useCallback((options) => {
        const audioElement = audioRef.current;
        if (!audioElement || !activeAudioFile) {
            return false;
        }
        if (controllerRef.current && selectedExpression) {
            controllerRef.current.setExpression(selectedExpression);
            setSelectedExpression('');
        }
        if (options?.restart) {
            audioElement.pause();
            audioElement.currentTime = 0;
        }
        const currentSequence = sequenceRef.current;
        const desiredRate = (currentSequence?.playbackRate ?? playbackRate) || 1;
        audioElement.playbackRate = desiredRate;
        const preserve = (currentSequence?.preservePitch ?? preservePitch) !== false;
        if ('preservesPitch' in audioElement) {
            audioElement.preservesPitch = preserve;
        }
        if ('mozPreservesPitch' in audioElement) {
            audioElement.mozPreservesPitch = preserve;
        }
        if ('webkitPreservesPitch' in audioElement) {
            audioElement.webkitPreservesPitch = preserve;
        }
        const resumePromise = audioContextRef.current?.state === 'suspended'
            ? audioContextRef.current.resume()
            : Promise.resolve();
        resumePromise
            .catch(err => {
            console.warn('AudioContext resume failed before play', err);
        })
            .finally(() => {
            audioElement.play().catch(err => {
                console.error('Audio playback failed', err);
            });
        });
        return true;
    }, [activeAudioFile, selectedExpression, playbackRate, preservePitch]);
    const handlePlayPause = useCallback(() => {
        const audioElement = audioRef.current;
        if (!audioElement || !activeAudioFile) {
            return;
        }
        if (isPlaying) {
            audioElement.pause();
        }
        else {
            startSequencePlayback({ restart: false });
        }
    }, [activeAudioFile, isPlaying, startSequencePlayback]);
    useEffect(() => {
        if (!autoPlayToken) {
            return;
        }
        startSequencePlayback({ restart: true });
    }, [autoPlayToken, startSequencePlayback]);
    useEffect(() => {
        if (!stopToken) {
            return;
        }
        const audioElement = audioRef.current;
        if (audioElement) {
            audioElement.pause();
            audioElement.currentTime = 0;
        }
        controllerRef.current?.setLipSyncValue(0);
        setIsPlaying(false);
    }, [stopToken]);
    const handleTriggerMotion = () => {
        if (!isLive2DReady || !controllerRef.current)
            return;
        const [group, indexStr] = selectedMotionKey.split(':');
        const index = Number(indexStr);
        controllerRef.current.startMotion(group, index);
    };
    return (_jsxs("div", { style: {
            width: '100%',
            height: '100%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
        }, children: [_jsxs("div", { style: {
                    flex: '1 1 auto',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '600px'
                }, children: [_jsx("canvas", { ref: canvasRef, style: {
                            width: '100%',
                            height: '100%',
                            maxWidth: '960px',
                            maxHeight: '720px',
                            display: 'block',
                            backgroundColor: '#ffffff'
                        } }), overlay && (_jsx("div", { style: {
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none'
                        }, children: _jsx("div", { style: { pointerEvents: 'auto', width: '100%', height: '100%' }, children: overlay }) }))] }), !isLive2DReady && !error && (_jsx(Overlay, { message: "\u30E2\u30C7\u30EB\u3092\u8AAD\u307F\u8FBC\u3093\u3067\u3044\u307E\u3059\u2026" })), error && _jsx(Overlay, { message: `Live2D初期化エラー: ${error}` }), _jsxs("div", { style: controlsStyle, children: [_jsx("button", { onClick: handlePlayPause, disabled: !activeAudioFile || !isLive2DReady, style: primaryButtonStyle, children: sequence
                            ? isPlaying
                                ? 'シーケンス一時停止'
                                : 'シーケンス再生'
                            : isPlaying
                                ? '一時停止'
                                : '再生' }), _jsx("span", { style: {
                            color: '#ffffff',
                            textShadow: '0 0 4px #000',
                            marginTop: isCompactLayout ? '4px' : 0,
                            display: isCompactLayout ? 'block' : 'inline-block'
                        }, children: statusLabel }), _jsxs("div", { style: controlGroupStyle, children: [_jsx("label", { htmlFor: "expression-select", style: { color: '#fff' }, children: "\u8868\u60C5:" }), _jsxs("select", { id: "expression-select", value: selectedExpression, onChange: event => setSelectedExpression(event.target.value), style: {
                                    padding: '6px 8px',
                                    minWidth: '160px',
                                    width: isCompactLayout ? '100%' : undefined
                                }, disabled: !isLive2DReady, children: [_jsx("option", { value: '', children: "\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044" }), ['Angry', 'Blushing', 'ExtremeSmile', 'f01', 'f02', 'Normal', 'Sad', 'Smile', 'Surprised'].map(name => (_jsx("option", { value: name, children: name }, name)))] }), _jsx("button", { onClick: () => {
                                    if (controllerRef.current && selectedExpression) {
                                        controllerRef.current.setExpression(selectedExpression);
                                        setSelectedExpression('');
                                    }
                                }, disabled: !isLive2DReady || !selectedExpression, style: {
                                    padding: '8px 14px',
                                    cursor: isLive2DReady && selectedExpression ? 'pointer' : 'not-allowed',
                                    backgroundColor: '#8e44ad',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    width: isCompactLayout ? '100%' : undefined
                                }, children: "\u8868\u60C5\u9069\u7528" })] }), _jsxs("div", { style: controlGroupStyle, children: [_jsx("label", { htmlFor: "motion-select", style: { color: '#fff' }, children: "\u30E2\u30FC\u30B7\u30E7\u30F3:" }), _jsx("select", { id: "motion-select", value: selectedMotionKey, onChange: event => setSelectedMotionKey(event.target.value), style: {
                                    padding: '6px 8px',
                                    minWidth: '160px',
                                    width: isCompactLayout ? '100%' : undefined
                                }, disabled: !isLive2DReady, children: MOTION_OPTIONS.map(option => (_jsx("option", { value: `${option.group}:${option.index}`, children: option.label }, `${option.group}:${option.index}`))) }), _jsx("button", { onClick: handleTriggerMotion, disabled: !isLive2DReady, style: {
                                    padding: '8px 14px',
                                    cursor: isLive2DReady ? 'pointer' : 'not-allowed',
                                    backgroundColor: '#1f78d1',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    width: isCompactLayout ? '100%' : undefined
                                }, children: "\u30E2\u30FC\u30B7\u30E7\u30F3\u518D\u751F" })] })] }), _jsx("audio", { ref: audioRef })] }));
}
function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}
function Overlay({ message }) {
    return (_jsx("div", { style: {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            color: 'white',
            fontSize: '18px'
        }, children: message }));
}
